/**
 * AwaitingDecisionFrame — wraps an in-chat proposal (edit highlight,
 * ad-hoc draft, agent write approval) and adds one mono status line
 * under the card: a violet dot + "Awaiting your approval · Word
 * unchanged until you approve".
 *
 * The three proposal adapters render the shared ``ProposalCard``; this
 * frame gives the pending state the same small-caps meta register the
 * film uses for statuses, so a proposal waiting on the writer reads
 * differently from a finished reply (which ends with the green
 * "Applied to the document" pill once decided).
 *
 * SRP: pure presentation. ``show`` is decided by the caller from the
 * proposal phase it already has; nothing here changes what Approve or
 * Reject do.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { brandColors, fonts, inkColors } from "../tokens";

export interface AwaitingDecisionFrameProps {
  children: React.ReactNode;
  /** Show the status line (false while applying / revising / disabled). */
  show: boolean;
  /** Override the status copy. */
  label?: string;
}

export const AWAITING_DECISION_LABEL = "Awaiting your approval · Word unchanged until you approve";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    alignSelf: "stretch",
    rowGap: "5px",
  },
  note: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    paddingLeft: "2px",
    fontFamily: fonts.mono,
    fontSize: "9.5px",
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    color: inkColors.ink4,
  },
  dot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    flexShrink: 0,
    background: brandColors.primary,
    animationName: {
      "0%": { opacity: 0.35 },
      "50%": { opacity: 1 },
      "100%": { opacity: 0.35 },
    },
    animationDuration: "1600ms",
    animationIterationCount: "infinite",
    animationTimingFunction: "ease-in-out",
    "@media (prefers-reduced-motion: reduce)": { animationName: "none", opacity: 1 },
  },
});

export const AwaitingDecisionFrame: React.FC<AwaitingDecisionFrameProps> = ({ children, show, label }) => {
  const styles = useStyles();
  return (
    <div className={styles.root}>
      {children}
      {show ? (
        <span className={styles.note}>
          <span className={styles.dot} aria-hidden="true" />
          {label ?? AWAITING_DECISION_LABEL}
        </span>
      ) : null}
    </div>
  );
};

export default AwaitingDecisionFrame;
